import { useAiSuggestStatusChecker } from '@/features/invoices/components/Details/Components/Context/AiSuggestStatus';
import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useTranslation } from '@/i18n';
import { useMemo } from 'react';

/**
 * 作業ステータス label when the `payment-request-support-agent` flex feature is
 * ON. 作業ステータス can show multiple in-progress statuses at once: OCR読取り中
 * is shown first and AIサジェスト処理中 is placed behind it while AI Suggest is
 * running, separated by a half-width comma. Once nothing is processing, the
 * underlying operation label (e.g. 未着手) is shown instead.
 *
 * The caller is responsible for the flag branch (flag OFF → use the plain
 * operation label instead of this hook's result).
 */
export const useDisplayedOperationLabel = (operationLabel: string): string => {
  const { t } = useTranslation();
  const { isProcessing: isAiSuggestProcessing } = useAiSuggestStatusChecker();
  const { isOcrProcessing } = useOperationStatusChecker();
  return useMemo(() => {
    if (!isAiSuggestProcessing) {
      return operationLabel;
    }
    const labels: string[] = [];
    if (isOcrProcessing) {
      // While OCR is processing, `operationLabel` is already "OCR読取り中".
      labels.push(operationLabel);
    }
    labels.push(t('operation_status_ai_suggest_processing'));
    return labels.join(', ');
  }, [isAiSuggestProcessing, isOcrProcessing, operationLabel, t]);
};
